import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatSAR, formatDate, statusColor } from "@/lib/utils";
import { BookingTable } from "./CustomerPages";
import PublicBookingForm from "./BookingWizard";
import { Building2, Wallet, ClipboardList, Users, FileText, Download } from "lucide-react";

function StatCard({ label, value, icon: Icon, color = "emerald" }) {
  const colors = { emerald: "bg-emerald-50 text-emerald-700", sky: "bg-sky-50 text-sky-700", amber: "bg-amber-50 text-amber-700", violet: "bg-violet-50 text-violet-700" };
  return (
    <div className="abdan-card p-5">
      <div className="flex items-center justify-between">
        <div><div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div><div className="text-2xl font-bold mt-1">{value}</div></div>
        <div className={`w-12 h-12 rounded-xl grid place-items-center ${colors[color]}`}><Icon className="w-5 h-5" /></div>
      </div>
    </div>
  );
}

export function PartnerDashboard() {
  const { user } = useApp();
  const [summary, setSummary] = useState({});
  const [bookings, setBookings] = useState([]);
  useEffect(() => {
    api.get("/reports/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data.items));
  }, []);
  return (
    <div className="space-y-6">
      <div><h1 className="text-3xl font-bold">Welcome back, {user.company || user.name}</h1><p className="text-slate-500">Corporate travel dashboard</p></div>
      <div className="grid md:grid-cols-4 gap-4">
        <StatCard label="Total Bookings" value={summary.total_bookings || 0} icon={ClipboardList} color="emerald" />
        <StatCard label="Completed" value={summary.completed || 0} icon={ClipboardList} color="sky" />
        <StatCard label="Revenue" value={formatSAR(summary.revenue || 0)} icon={Wallet} color="violet" />
        <StatCard label="Pending" value={summary.pending || 0} icon={ClipboardList} color="amber" />
      </div>
      <Card><CardHeader><CardTitle>Recent Bookings</CardTitle></CardHeader>
        <CardContent><BookingTable rows={bookings.slice(0, 8)} /></CardContent></Card>
    </div>
  );
}

export function PartnerNewBooking() { return <div className="-mx-4 md:-mx-8 -my-4 md:-my-8"><PublicBookingForm /></div>; }

export function PartnerMyBooking() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/bookings").then((r) => setRows(r.data.items)); }, []);
  return <div className="space-y-4"><h1 className="text-3xl font-bold">My Bookings</h1><Card><CardContent className="p-4"><BookingTable rows={rows} /></CardContent></Card></div>;
}

export function PartnerCustomers() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/bookings").then((r) => {
    const map = {};
    r.data.items.forEach((b) => { if (!map[b.customer_email]) map[b.customer_email] = { name: b.customer_name, email: b.customer_email, phone: b.customer_phone, count: 0, spend: 0 };
      map[b.customer_email].count++; if (b.payment_status === "paid") map[b.customer_email].spend += b.price; });
    setRows(Object.values(map));
  }); }, []);
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">Customers</h1>
      <Card><CardContent className="p-4">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Bookings</TableHead><TableHead>Spend</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No customers yet.</TableCell></TableRow>}
            {rows.map((c, i) => (<TableRow key={i}><TableCell className="font-medium">{c.name}</TableCell><TableCell>{c.email}</TableCell><TableCell>{c.phone}</TableCell><TableCell>{c.count}</TableCell><TableCell className="font-semibold text-emerald-700">{formatSAR(c.spend)}</TableCell></TableRow>))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

export function PartnerInvoice() {
  const { user } = useApp();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/bookings").then((r) => setRows(r.data.items.filter((b) => b.payment_status === "paid"))); }, []);
  const total = rows.reduce((s, b) => s + b.price, 0);
  const vat = total * 0.15;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between"><h1 className="text-3xl font-bold">Invoices (ZATCA)</h1>
        <Button onClick={() => window.print()} data-testid="btn-print-invoice"><Download className="w-4 h-4 mr-2" />Print</Button></div>
      <Card><CardContent className="p-8">
        <div className="flex items-start justify-between border-b pb-6 mb-6">
          <div><div className="text-xs uppercase text-slate-500 tracking-widest">Tax Invoice</div><h2 className="text-2xl font-bold mt-1">Abdan Pro Transport</h2><div className="text-sm text-slate-500">VAT No. 3xxxxxxxxxxxxx</div></div>
          <div className="text-right"><div className="text-xs uppercase text-slate-500">Billed To</div><div className="font-bold">{user.company || user.name}</div><div className="text-sm">{user.email}</div></div>
        </div>
        <Table><TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Route</TableHead><TableHead>Date</TableHead><TableHead>Amount</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((b) => (<TableRow key={b.id}><TableCell className="font-mono">{b.code}</TableCell><TableCell>{b.origin} → {b.destination}</TableCell><TableCell>{formatDate(b.pickup_date)}</TableCell><TableCell className="font-semibold">{formatSAR(b.price)}</TableCell></TableRow>))}</TableBody></Table>
        <div className="flex justify-end mt-6"><div className="w-80 space-y-2">
          <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{formatSAR(total)}</span></div>
          <div className="flex justify-between text-slate-600"><span>VAT (15%)</span><span>{formatSAR(vat)}</span></div>
          <div className="flex justify-between font-bold text-xl border-t pt-2"><span>Total</span><span className="text-emerald-700">{formatSAR(total + vat)}</span></div>
        </div></div>
      </CardContent></Card>
    </div>
  );
}

export function PartnerReports() {
  const [summary, setSummary] = useState({});
  useEffect(() => { api.get("/reports/summary").then((r) => setSummary(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">Reports</h1>
      <div className="grid md:grid-cols-4 gap-4">
        <StatCard label="Total Bookings" value={summary.total_bookings || 0} icon={ClipboardList} color="emerald" />
        <StatCard label="Completed" value={summary.completed || 0} icon={ClipboardList} color="sky" />
        <StatCard label="Revenue" value={formatSAR(summary.revenue || 0)} icon={Wallet} color="violet" />
        <StatCard label="Pending" value={summary.pending || 0} icon={ClipboardList} color="amber" />
      </div>
    </div>
  );
}
